(() => {
  const reviewPageSize = 3;
  const initialize = (root) => {
    if (root.dataset.reviewsReady) return;
    root.dataset.reviewsReady = 'true';
    const find = (selector) => root.querySelector(selector);
    const write = find('.x13-dlg--write');
    const summary = find('.x13-dlg--sum');
    const form = find('[data-x13-form]');
    const reviewPhoto = find('.x13-product img');
    const galleryPhoto = document.querySelector('[data-pp-gallery-slot="1"] img');
    if (reviewPhoto && galleryPhoto) {
      const photoURL = new URL(galleryPhoto.getAttribute('src'), location.href);
      photoURL.searchParams.set('width', '104');
      reviewPhoto.removeAttribute('srcset');
      reviewPhoto.src = photoURL.href;
    }
    const appKey = root.dataset.appKey;
    const productId = root.dataset.productId;
    const productDataURL = `https://api-cdn.yotpo.com/v3/storefront/stores/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/productData`;
    const cacheKey = `sap-reviews:v1:${appKey}:${productId}`;
    let opener;
    let step = 1;
    let submitting = false;
    let completed = false;
    let questionPromise;
    let summaryPromise;
    let bottomlinePromise;
    const getJSON = async (url, options = {}) => {
      const response = await fetch(url, { ...options, credentials: 'omit', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Review service unavailable');
      const body = await response.json();
      if (body.status?.code && body.status.code >= 400) throw new Error('Review service unavailable');
      return body;
    };
    const getQuestions = () => {
      questionPromise ||= getJSON(`${productDataURL}?include=customQuestions`).then((body) => body.response?.customQuestions || []).catch(() => {
        questionPromise = null;
        return [];
      });
      return questionPromise;
    };
    const openDialog = (dialog, button) => {
      opener = button;
      dialog.showModal();
      document.documentElement.classList.add('sap-reviews-open');
      dialog.querySelector('.x13-body').scrollTop = 0;
      dialog.querySelector('h2').focus();
    };
    for (const dialog of [write, summary]) {
      dialog.querySelectorAll('[data-x13-close]').forEach((button) => button.addEventListener('click', () => dialog.close()));
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('sap-reviews-open');
        opener?.focus();
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab') return;
        const controls = [...dialog.querySelectorAll('button, input, textarea, select, a[href], [tabindex="0"]')].filter((control) => !control.disabled && control.getClientRects().length);
        const first = controls[0];
        const last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      });
    }
    find('[data-x13-write]').addEventListener('click', (event) => {
      openDialog(write, event.currentTarget);
      if (completed) find('[data-x13-done]').focus();
      getQuestions();
    });
    const showStep = (nextStep) => {
      step = nextStep;
      find('[data-x13-step="1"]').hidden = step !== 1;
      find('[data-x13-step="2"]').hidden = step !== 2;
      find('[data-step-one]').hidden = step !== 1;
      find('[data-step-two]').hidden = step !== 2;
      find('[data-x13-back]').hidden = step !== 2;
      find('[data-x13-next]').hidden = step !== 1;
      find('[data-x13-send]').hidden = step !== 2;
      write.querySelector('.x13-body').scrollTop = 0;
      (step === 2 ? find('.x13-more') : write.querySelector('h2')).focus();
    };
    const validate = () => {
      let firstInvalid;
      for (const group of form.querySelectorAll('[data-review-required]')) {
        const control = group.querySelector('input, textarea');
        const valid = control.type === 'radio' ? !!group.querySelector(':checked') : !!control.value.trim() && control.checkValidity();
        group.querySelector('.x13-error').hidden = valid;
        group.querySelectorAll('input, textarea').forEach((field) => field.setAttribute('aria-invalid', String(!valid)));
        if (!valid && !firstInvalid) firstInvalid = control;
      }
      if (firstInvalid) {
        showStep(1);
        firstInvalid.focus();
      }
      return !firstInvalid;
    };
    find('[data-x13-next]').addEventListener('click', () => { if (validate()) showStep(2); });
    find('[data-x13-back]').addEventListener('click', () => { if (!submitting) showStep(1); });
    const concerns = [...form.querySelectorAll('[name="concern"]')];
    concerns.forEach((input) => input.addEventListener('change', () => {
      if (concerns.filter((option) => option.checked).length > 2) input.checked = false;
      const atLimit = concerns.filter((option) => option.checked).length >= 2;
      concerns.forEach((option) => { option.disabled = atLimit && !option.checked; });
      find('[data-concern-limit]').hidden = !atLimit;
    }));
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submitting || completed || !validate()) return;
      if (step !== 2) { showStep(2); return; }
      submitting = true;
      find('[data-submit-error]').hidden = true;
      find('[data-x13-send]').disabled = true;
      find('[data-x13-back]').disabled = true;
      const status = find('[data-submit-status]');
      status.textContent = status.dataset.sending;
      const values = new FormData(form);
      const payload = {
        appkey: appKey,
        sku: productId,
        product_title: root.dataset.productTitle,
        product_url: root.dataset.productUrl,
        display_name: values.get('display_name').trim(),
        email: values.get('email').trim(),
        review_content: values.get('review_content').trim(),
        review_title: values.get('review_title').trim(),
        review_score: Number(values.get('review_score')),
        custom_fields: {},
      };
      try {
        const questions = await getQuestions();
        const titles = { age: ['Age range'], skin: ['Skin type'], concern: ['Skin concern', 'Skin concerns', 'Main skin concerns'] };
        for (const [name, aliases] of Object.entries(titles)) {
          const question = questions.find((entry) => aliases.some((title) => String(entry.title || '').trim().toLowerCase() === title.toLowerCase()));
          const selected = values.getAll(name).slice(0, name === 'concern' ? 2 : 1);
          if (question?.questionId && selected.length) {
            const questionKey = String(question.questionId).replace(/^--/, '');
            const options = Array.isArray(question.options) ? question.options : [];
            const answers = selected.map((answer) => options.find((option) => typeof option === 'string' && option.toLowerCase() === answer.toLowerCase()) ?? answer);
            payload.custom_fields[`--${questionKey}`] = name === 'concern' ? answers : answers[0];
          }
        }
        if (!appKey) throw new Error('Review service unavailable');
        const response = await fetch('https://api.yotpo.com/v1/widget/reviews', { method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload), signal: AbortSignal.timeout(20000) });
        const result = await response.json();
        const statusCode = Number(result.status?.code);
        if (!response.ok || !Number.isFinite(statusCode) || statusCode < 200 || statusCode >= 300) throw new Error('Review submission failed');
        completed = true;
        find('[data-x13-step="1"]').hidden = true;
        find('[data-x13-step="2"]').hidden = true;
        find('.x13-step').hidden = true;
        find('[data-form-foot]').hidden = true;
        find('[data-x13-done]').hidden = false;
        form.reset();
        find('[data-x13-done]').focus();
      } catch {
        find('[data-submit-error]').hidden = false;
      } finally {
        submitting = false;
        status.textContent = '';
        find('[data-x13-send]').disabled = false;
        find('[data-x13-back]').disabled = false;
      }
    });
    const validSummary = (value) => value && value.data && value.bottomline && Number.isFinite(Number(value.bottomline.total_review));
    const validBottomline = (value) => value && value.total_review != null && Number.isFinite(Number(value.total_review)) && Number(value.total_review) >= 0;
    const readCache = (key) => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(key));
        if (saved && saved.expires > Date.now() && saved.expires <= Date.now() + 3600000) return saved.value;
      } catch {}
      return null;
    };
    const loadBottomline = () => {
      const saved = readCache(`${cacheKey}:bottomline`) || readCache(cacheKey)?.bottomline;
      if (validBottomline(saved)) return Promise.resolve(saved);
      bottomlinePromise ||= getJSON(`https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/reviews.json`).then((body) => {
        const bottomline = body.response?.bottomline;
        if (!validBottomline(bottomline)) throw new Error('Review ratings unavailable');
        try { sessionStorage.setItem(`${cacheKey}:bottomline`, JSON.stringify({ expires: Date.now() + 3600000, value: bottomline })); } catch {}
        renderBars(bottomline);
        return bottomline;
      }).finally(() => { bottomlinePromise = null; });
      return bottomlinePromise;
    };
    const renderBars = (bottomline) => {
      const total = Number(bottomline.total_review);
      const average = Number(bottomline.average_score);
      if (total > 0 && (bottomline.average_score == null || !Number.isFinite(average) || average < 0 || average > 5)) return;
      const score = find('.x1-rev__score');
      const stars = score.querySelector('.x1-stars');
      score.hidden = total === 0;
      find('[data-review-bars]').hidden = total === 0;
      find('[data-x13-sum]').hidden = total === 0;
      find('[data-review-list]').hidden = total === 0;
      find('.x1-rev__sum').classList.toggle('x1-rev__sum--empty', total === 0);
      if (bottomline.average_score != null && Number.isFinite(average)) {
        const displayed = (Math.round(average * 10) / 10).toFixed(1);
        find('[data-review-score]').textContent = displayed;
        stars.setAttribute('aria-label', score.dataset.rated.replaceAll('__N__', displayed));
        stars.querySelectorAll('svg').forEach((star, index) => star.classList.toggle('is-off', index + 1 > Math.round(average)));
      }
      find('[data-review-count]').textContent = score.dataset.basedOn.replaceAll('__N__', total);
      root.querySelectorAll('[data-review-stars]').forEach((row) => {
        const value = bottomline.star_distribution?.[row.dataset.reviewStars];
        const amount = Number(value);
        if (value == null || !Number.isFinite(amount) || amount < 0) return;
        row.querySelector('[data-review-bar-count]').textContent = amount;
        row.querySelector('[data-review-bar-fill]').style.width = `${total ? Math.min(100, amount / total * 100) : 0}%`;
      });
    };
    if (find('[data-review-bars]')) loadBottomline().then(renderBars).catch(() => {});
    const loadSummary = async () => {
      try {
        const saved = JSON.parse(sessionStorage.getItem(cacheKey));
        if (saved && saved.expires > Date.now() && saved.expires <= Date.now() + 3600000 && validSummary(saved.value)) return saved.value;
      } catch {}
      const [data, bottomline] = await Promise.all([
        getJSON(`${productDataURL}?include=topics,reviewsSummary`),
        loadBottomline(),
      ]);
      const value = { data: data.response, bottomline };
      if (!validSummary(value)) throw new Error('Review summary unavailable');
      try { sessionStorage.setItem(cacheKey, JSON.stringify({ expires: Date.now() + 3600000, value })); } catch {}
      return value;
    };
    const createText = (tag, text, className) => {
      const element = document.createElement(tag);
      element.textContent = text;
      if (className) element.className = className;
      return element;
    };
    const listArea = find('[data-review-list]');
    const reviewList = find('[data-x1-revlist]');
    if (reviewList) {
      const filterButton = find('[data-x1-filters]');
      const chips = find('.x1-rev__chips');
      const sortSelect = find('[data-x1-sort]');
      const moreButton = find('[data-x1-more]');
      const retryButton = find('[data-review-retry]');
      const emptyMessage = find('.x1-rev__none');
      const listStatus = find('[data-review-list-status]');
      const listCache = new Map();
      const sorts = { relevant: ['votes_up', 'desc', 'votes_up'], new: ['date', 'desc', 'date'], high: ['rating', 'desc', 'score'], low: ['rating', 'asc', 'score'] };
      let concernQuestion;
      let pageNumber = 0;
      let revision = 0;
      let started = false;
      let failedRequest;
      const dateFormatter = new Intl.DateTimeFormat(listArea.dataset.locale || document.documentElement.lang || 'en', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
      const renderReview = (review) => {
        const item = createText('li', '', 'x1-rev__item');
        const score = Math.max(0, Math.min(5, Number(review.score) || 0));
        item.dataset.stars = score;
        item.dataset.when = review.created_at || '';
        item.dataset.reviewId = review.id;
        item.tabIndex = -1;
        const who = createText('div', '', 'x1-rev__who');
        who.append(createText('span', review.user?.display_name || '', 'x1-rev__name'));
        if (review.verified_buyer === true) who.append(createText('span', listArea.dataset.verified, 't-label x1-rev__ver'));
        const date = new Date(review.created_at);
        if (Number.isFinite(date.getTime())) who.append(createText('span', dateFormatter.format(date), 'x1-rev__date'));
        const say = createText('div', '', 'x1-rev__say');
        const stars = find('.x1-rev__score .x1-stars').cloneNode(true);
        stars.setAttribute('aria-label', listArea.dataset.starsLabel.replaceAll('__N__', score));
        stars.querySelectorAll('svg').forEach((star, index) => star.classList.toggle('is-off', index + 1 > Math.round(score)));
        say.append(stars);
        if (review.title) say.append(createText('blockquote', review.title, 'x1-rev__title'));
        say.append(createText('blockquote', review.content || '', 'x1-rev__words'));
        const answers = Object.values(review.custom_fields || {}).flatMap((field) => Array.isArray(field?.value) ? field.value : [field?.value]).filter((value) => typeof value === 'string' && value.trim());
        if (answers.length) say.append(createText('p', answers.join(' · '), 't-small x1-rev__tags'));
        item.append(who, say);
        return item;
      };
      const loadReviews = async (append = false, announce = false, retry = null) => {
        const currentRevision = ++revision;
        const requestControl = retry ? retryButton : append ? moreButton : null;
        const restoreRetryFocus = requestControl === document.activeElement && requestControl.matches(':focus-visible');
        const nextPage = retry?.nextPage ?? (append ? pageNumber + 1 : 1);
        const selected = retry?.selected ?? [...chips.querySelectorAll('[aria-pressed="true"]')].map((chip) => chip.textContent.trim());
        const [sort, direction, sortBy] = retry?.sorting ?? sorts[sortSelect.value] ?? sorts.relevant;
        const cacheId = JSON.stringify([sort, direction, selected, nextPage]);
        failedRequest = null;
        retryButton.hidden = true;
        retryButton.disabled = true;
        moreButton.disabled = true;
        moreButton.hidden = true;
        emptyMessage.hidden = true;
        listStatus.textContent = '';
        listStatus.classList.add('visually-hidden');
        listStatus.classList.remove('x13-error');
        reviewList.setAttribute('aria-busy', 'true');
        try {
          if (!listCache.has(cacheId)) {
            let request;
            if (selected.length && concernQuestion) {
              const options = Array.isArray(concernQuestion.options) ? concernQuestion.options : [];
              const answers = selected.map((answer) => options.find((option) => typeof option === 'string' && option.toLowerCase() === answer.toLowerCase()) ?? answer);
              request = getJSON(`https://api-cdn.yotpo.com/v1/reviews/${encodeURIComponent(appKey)}/filter.json?page=${nextPage}&per_page=${reviewPageSize}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ domain_key: productId, scores: [], crfs: [{ question_id: concernQuestion.questionId, answers }], sortings: [{ sort_by: sortBy, ascending: direction === 'asc' }], per_page: reviewPageSize }),
              });
            } else {
              request = getJSON(`https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/reviews.json?per_page=${reviewPageSize}&page=${nextPage}&sort=${sort}&direction=${direction}`);
            }
            listCache.set(cacheId, request.then((body) => {
              const data = body.response;
              if (!Array.isArray(data?.reviews) || data.pagination?.total == null || !Number.isFinite(Number(data.pagination.total)) || Number(data.pagination.total) < 0) throw new Error('Reviews unavailable');
              if (selected.length && data.found_filtered_reviews === false) return { reviews: [], pagination: { total: 0 } };
              return data;
            }).catch((error) => { listCache.delete(cacheId); throw error; }));
          }
          const data = await listCache.get(cacheId);
          if (currentRevision !== revision) return;
          if (!append) reviewList.replaceChildren();
          const seen = new Set([...reviewList.children].map((item) => item.dataset.reviewId));
          const items = data.reviews.filter((review) => {
            const id = String(review.id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          }).map(renderReview);
          reviewList.append(...items);
          pageNumber = nextPage;
          const total = Number(data.pagination.total);
          emptyMessage.hidden = !selected.length || total !== 0;
          moreButton.hidden = reviewList.children.length >= total || data.reviews.length === 0;
          if (announce) listStatus.textContent = find('.x1-rev__score').dataset.basedOn.replaceAll('__N__', total);
          if (append) items[0]?.focus();
          else if (retry) reviewList.focus({ preventScroll: true });
        } catch {
          if (currentRevision !== revision) return;
          failedRequest = { nextPage, selected, sorting: [sort, direction, sortBy], append };
          listStatus.classList.remove('visually-hidden');
          listStatus.classList.add('x13-error');
          listStatus.textContent = listStatus.dataset.error;
          retryButton.hidden = false;
          retryButton.disabled = false;
          if (restoreRetryFocus) retryButton.focus();
        } finally {
          if (currentRevision === revision) {
            moreButton.disabled = false;
            reviewList.setAttribute('aria-busy', 'false');
          }
        }
      };
      filterButton.addEventListener('click', () => {
        chips.hidden = !chips.hidden;
        filterButton.setAttribute('aria-expanded', String(!chips.hidden));
      });
      chips.querySelectorAll('button').forEach((chip) => chip.addEventListener('click', () => {
        chip.setAttribute('aria-pressed', String(chip.getAttribute('aria-pressed') !== 'true'));
        loadReviews(false, true);
      }));
      sortSelect.addEventListener('change', () => loadReviews(false, true));
      moreButton.addEventListener('click', () => loadReviews(true, true));
      retryButton.addEventListener('click', () => {
        if (!failedRequest) return;
        const request = failedRequest;
        loadReviews(request.append, true, request);
      });
      const startList = () => {
        if (started) return;
        started = true;
        getQuestions().then((questions) => {
          concernQuestion = questions.find((question) => ['skin concern', 'skin concerns', 'main skin concerns'].includes(String(question.title || '').trim().toLowerCase()));
          filterButton.hidden = !concernQuestion?.questionId;
        });
        loadReviews();
      };
      loadBottomline().then((bottomline) => { listArea.hidden = Number(bottomline.total_review) === 0; }).catch(() => {});
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); startList(); }
        }, { rootMargin: `${window.innerHeight}px 0px` });
        observer.observe(root);
      } else startList();
    }
    const renderSummary = ({ data, bottomline }) => {
      renderBars(bottomline);
      const total = Number(bottomline.total_review);
      const hasSummary = typeof data.paragraphSummary === 'string' && !!data.paragraphSummary.trim();
      const count = find('[data-summary-count]');
      count.textContent = (count.hasAttribute('data-summary-live-count') ? total > 0 : hasSummary) ? count.dataset.countTemplate.replace('__COUNT__', total) : '';
      find('[data-summary-content]').hidden = !hasSummary;
      find('[data-summary-fallback]').hidden = hasSummary;
      find('[data-curated-quotes]').hidden = hasSummary || !find('[data-curated-quotes] figure') || total === 0;
      const highlights = Array.isArray(data.summaryHighlights) ? data.summaryHighlights.slice(0, 3) : [];
      find('[data-summary-quotes]').hidden = !hasSummary || !highlights.length;
      if (hasSummary) {
        find('[data-summary-lead]').textContent = data.paragraphSummary;
        const topics = find('[data-summary-topics]');
        topics.replaceChildren();
        for (const topic of Array.isArray(data.summaryTopics) ? data.summaryTopics : []) {
          const percent = Math.min(100, Math.max(0, parseFloat(topic.coverage) || 0));
          const row = createText('li', '');
          row.append(createText('strong', topic.displayTopic || topic.name || topic.topic || topic.title || ''));
          if (topic.summary) row.append(createText('p', topic.summary, 'x13-topic-summary'));
          row.append(createText('span', topics.dataset.mentionedTemplate.replace('__MENTIONS__', Math.round(percent * total / 100)).replace('__TOTAL__', total)));
          topics.append(row);
        }
        const quotes = find('[data-highlight-quotes]');
        quotes.replaceChildren();
        for (const highlight of highlights) {
          const quote = createText('figure', '', 'x13-quote');
          quote.dataset.reviewId = highlight.reviewId || '';
          quote.append(createText('blockquote', highlight.highlight || ''));
          quote.append(createText('figcaption', String(highlight.reviewerName || '').trim().split(/\s+/)[0]));
          quotes.append(quote);
        }
      } else {
        const split = find('[data-star-split]');
        split.replaceChildren();
        for (let stars = 5; stars >= 1; stars -= 1) {
          const amount = Math.max(0, Number(bottomline.star_distribution?.[stars]) || 0);
          const row = createText('li', '');
          row.append(createText('span', split.dataset.starsTemplate.replace('__STARS__', stars).replace('__COUNT__', amount)));
          const bar = document.createElement('span');
          bar.className = 'x13-bar';
          bar.setAttribute('aria-hidden', 'true');
          const fill = document.createElement('span');
          fill.style.width = `${total ? Math.min(100, amount / total * 100) : 0}%`;
          bar.append(fill);
          row.append(bar);
          split.append(row);
        }
      }
    };
    find('[data-x13-sum]').addEventListener('click', async (event) => {
      openDialog(summary, event.currentTarget);
      find('[data-summary-loading]').hidden = false;
      find('[data-summary-error]').hidden = true;
      root.querySelectorAll('[data-summary-content], [data-summary-fallback], [data-summary-quotes], [data-curated-quotes]').forEach((element) => { element.hidden = true; });
      find('[data-summary-count]').textContent = '';
      try {
        summaryPromise ||= loadSummary();
        renderSummary(await summaryPromise);
      } catch {
        summaryPromise = null;
        find('[data-summary-error]').hidden = false;
      } finally {
        find('[data-summary-loading]').hidden = true;
        summaryPromise = null;
      }
    });
    find('[data-x13-all]').addEventListener('click', () => {
      const list = find('[data-review-list]');
      opener = list;
      summary.close();
      list.focus({ preventScroll: true });
      list.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    });
  };
  const scan = () => document.querySelectorAll('[data-sap-reviews]').forEach(initialize);
  scan();
  document.addEventListener('shopify:section:load', scan);
})();
